import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '@/lib/api';
import type { StrategyTemplate, StrategyVersion, BacktestReport } from '@/types/api';

export function useStrategies(opts?: { refetchInterval?: number; exclude?: string }) {
  return useQuery<StrategyTemplate[]>({
    queryKey: ['strategies', { exclude: opts?.exclude }],
    queryFn: async () => {
      const params = opts?.exclude ? { exclude: opts.exclude } : undefined;
      const res = await api.get('/strategies', { params });
      return res.data;
    },
    refetchInterval: opts?.refetchInterval,
  });
}

export function useStrategy(id: string, opts?: { refetchInterval?: number }) {
  return useQuery<StrategyTemplate>({
    queryKey: ['strategies', id],
    queryFn: async () => {
      const res = await api.get(`/strategies/${id}`);
      return res.data;
    },
    enabled: !!id,
    refetchInterval: opts?.refetchInterval,
  });
}

/** Response from POST /strategies/analyze — now returns a job ID for async tracking */
export interface AnalyzeResult {
  ai_analysis: string;
  inferred_strategy_type: string;
  strategy_breakdown: Record<string, unknown>;
  stats: Record<string, unknown>;
  draft: { id: string; name: string; status: string; rules: unknown } | null;
}

export function useAnalyzeTrades() {
  return useMutation({
    mutationFn: async (formData: FormData) => {
      const res = await api.post('/strategies/analyze', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return res.data as { jobId: string };
    },
  });
}

export function useUpdateStrategy() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, name, description, symbol }: { id: string; name?: string; description?: string; symbol?: string }) => {
      const res = await api.patch(`/strategies/${id}`, { name, description, symbol });
      return res.data as StrategyTemplate;
    },
    onSuccess: (_data, { id }) => {
      qc.invalidateQueries({ queryKey: ['strategies'] });
      qc.invalidateQueries({ queryKey: ['strategies', id] });
    },
  });
}

export function usePublishStrategy() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, changeSummary }: { id: string; changeSummary?: string }) => {
      const res = await api.post(`/strategies/${id}/publish`, { change_summary: changeSummary });
      return res.data;
    },
    onSuccess: (_data, { id }) => {
      qc.invalidateQueries({ queryKey: ['strategies'] });
      qc.invalidateQueries({ queryKey: ['strategies', id] });
      qc.invalidateQueries({ queryKey: ['strategy-versions', id] });
    },
  });
}

export function useArchiveStrategy() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await api.post(`/strategies/${id}/archive`);
      return res.data;
    },
    onSuccess: (_data, id) => {
      qc.invalidateQueries({ queryKey: ['strategies'] });
      qc.invalidateQueries({ queryKey: ['strategies', id] });
    },
  });
}

export function useStrategyVersions(id: string) {
  return useQuery<Omit<StrategyVersion, 'rules'>[]>({
    queryKey: ['strategy-versions', id],
    queryFn: async () => {
      const res = await api.get(`/strategies/${id}/versions`);
      return res.data;
    },
    enabled: !!id,
  });
}

export function useStrategyVersion(id: string, versionNumber: number) {
  return useQuery<StrategyVersion>({
    queryKey: ['strategy-versions', id, versionNumber],
    queryFn: async () => {
      const res = await api.get(`/strategies/${id}/versions/${versionNumber}`);
      return res.data;
    },
    enabled: !!id && versionNumber > 0,
  });
}

export function useApplyVersion() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, versionNumber }: { id: string; versionNumber: number }) => {
      const res = await api.post(`/strategies/${id}/versions/${versionNumber}/apply`);
      return res.data as StrategyTemplate;
    },
    onSuccess: (_data, { id }) => {
      qc.invalidateQueries({ queryKey: ['strategies'] });
      qc.invalidateQueries({ queryKey: ['strategies', id] });
    },
  });
}

export function useResetStrategy() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await api.post(`/strategies/${id}/reset`);
      return res.data;
    },
    onSuccess: (_data, id) => {
      qc.invalidateQueries({ queryKey: ['strategies', id] });
    },
  });
}

export function useApplySuggestedRules() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await api.post(`/strategies/${id}/apply-suggested`);
      return res.data as StrategyTemplate;
    },
    onSuccess: (_data, id) => {
      qc.invalidateQueries({ queryKey: ['strategies'] });
      qc.invalidateQueries({ queryKey: ['strategies', id] });
    },
  });
}

export function useUpdateConfig() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, rules }: { id: string; rules: unknown }) => {
      const res = await api.post(`/strategies/${id}/update-config`, { rules });
      return res.data as StrategyTemplate;
    },
    onSuccess: (_data, { id }) => {
      qc.invalidateQueries({ queryKey: ['strategies'] });
      qc.invalidateQueries({ queryKey: ['strategies', id] });
    },
  });
}

export interface BacktestParams {
  symbol?: string;
  days?: number;
  capital?: number;
  timeframe?: string;
  leverage?: number;
  startDate?: string;
  endDate?: string;
  friction?: {
    enabled: boolean;
    spreadPoints?: number;
    commissionPerLot?: number;
    slippagePoints?: number;
    slippageModel?: string;
  };
}

export function useRunBacktest() {
  return useMutation({
    mutationFn: async ({ id, params }: { id: string; params: BacktestParams }) => {
      const res = await api.post(`/strategies/${id}/backtest`, params);
      return res.data as { jobId: string };
    },
  });
}

export interface VariantComparison {
  variant: 'original' | 'suggested' | 'current';
  returnPct: number;
  winRate: number;
  maxDrawdown: number;
  profitFactor: number;
  sharpeRatio: number;
  totalTrades: number;
  netProfit: number;
}

export interface ComparisonSummary {
  variants: VariantComparison[];
  bestBy: {
    returnPct: string;
    winRate: string;
    maxDrawdown: string;
    profitFactor: string;
    sharpeRatio: string;
  };
  rulesIdentical: boolean;
}

export interface ComparativeResult {
  config: Record<string, unknown>;
  original: BacktestReport | { error: string };
  suggested: BacktestReport | { error: string } | null;
  current: BacktestReport | { error: string };
  comparison?: ComparisonSummary;
}

export function useCreateStrategy() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: {
      name: string;
      symbol: string;
      description?: string;
      strategy_type: string;
      rules: unknown;
    }) => {
      const res = await api.post('/strategies/create', data);
      return res.data as StrategyTemplate;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['strategies'] });
    },
  });
}

export function useGenerateSuggestions() {
  return useMutation({
    mutationFn: async ({ id, mode, capital, leverage }: {
      id: string; mode: string; capital?: number; leverage?: number;
    }) => {
      const res = await api.post(`/strategies/${id}/generate-suggestions`, { mode, capital, leverage });
      return res.data as { jobId: string };
    },
  });
}

export type ChatResponse =
  | { type: 'rules'; rules: any; explanation: string; suggestedSymbol?: string; suggestions?: string[] }
  | { type: 'backtest'; explanation: string; params: { days: number; capital: number; leverage: number } }
  | { type: 'analysis'; explanation: string; keyPoints: Array<{ label: string; value: string; sentiment: 'positive' | 'negative' | 'neutral' }>; suggestions?: string[] }
  | { type: 'clarification'; question: string; options?: string[] }
  | { type: 'off_topic'; message: string };

export function useChatWithStrategy() {
  return useMutation({
    mutationFn: async (data: {
      prompt: string;
      currentRules?: unknown;
      conversationHistory?: Array<{ role: 'user' | 'assistant'; content: string }>;
      context?: { symbol?: string; capital?: number; leverage?: number; rulesModifiedManually?: boolean; strategyName?: string; strategyDescription?: string };
      backtestReport?: unknown;
      sessionId?: string;
    }) => {
      const res = await api.post('/strategies/chat', data);
      return res.data as ChatResponse;
    },
  });
}

// --- Chat Session Hooks ---

export interface ChatSessionMessage {
  id: string;
  role: string;
  content: string;
  message_type: string;
  rules_snapshot?: any;
  previous_rules?: any;
  rules_applied: boolean;
  metadata?: any;
  sequence: number;
  created_at: string;
}

export interface ChatSession {
  id: string;
  rules_snapshot_at_start: any;
  rules_snapshot_at_end: any;
  messages: ChatSessionMessage[];
  created_at: string;
  updated_at: string;
}

export function useChatSession(strategyId?: string) {
  return useQuery<ChatSession>({
    queryKey: ['chat-session', strategyId],
    queryFn: async () => {
      const res = await api.get(`/strategies/${strategyId}/chat/session`);
      return res.data;
    },
    enabled: !!strategyId,
  });
}

export function useDeleteChatSession() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (strategyId: string) => {
      const res = await api.delete(`/strategies/${strategyId}/chat/session`);
      return res.data;
    },
    onSuccess: (_, strategyId) => {
      qc.invalidateQueries({ queryKey: ['chat-session', strategyId] });
    },
  });
}

export function useStatelessBacktest() {
  return useMutation({
    mutationFn: async (data: {
      rules: unknown;
      symbol: string;
      days?: number;
      capital?: number;
      leverage?: number;
      timeframe?: string;
    }) => {
      const res = await api.post('/strategies/backtest', data);
      return res.data as { jobId: string };
    },
  });
}

export function useBacktestCompare() {
  return useMutation({
    mutationFn: async ({ id, params }: { id: string; params: BacktestParams }) => {
      const res = await api.post(`/strategies/${id}/backtest-compare`, params);
      return res.data as { jobId: string };
    },
  });
}
